import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useVertical } from './VerticalContext';
import { reportError } from '../lib/errors';
import { monthKey, monthRange } from '../lib/periods';

export type PotStatus = 'ok' | 'error' | 'loading' | 'unknown';

export interface PotStatusMap {
    safe_to_spend: PotStatus;
    tax_pot: PotStatus;
    ni_pot: PotStatus;
    vat_pot: PotStatus;
}

export interface WorkingCapital {
    parts_reserved: number;      // cost_price × qty on active/unpaid jobs
    parts_unreconciled: number;  // cost_price on paid jobs, supplier invoice not yet uploaded
    bills_due_30d: number;       // recurring expenses normalised to next 30 days
    total: number;
}

export interface FinancialData {
    safe_to_spend: number;      // GROSS — before the working-capital reserve (from SQL)
    safe_to_spend_net: number;  // NET — gross minus working capital, floored at 0 (from SQL)
    underfunded_by: number;     // amount by which working capital exceeds gross STS (from SQL)
    is_underfunded: boolean;    // true when working capital owed exceeds gross STS (from SQL)
    working_capital: WorkingCapital;
    tax_pot: number;
    ni_pot: number;
    vat_pot: number;
    raw_vat_liability: number;
    total_income: number;
    total_expenses: number;
    gross_income: number;
    gross_expenses: number;
    cash_balance: number;
    turnover_rolling_12m: number;
    vat_threshold: number;
    is_vat_registered: boolean;
    vat_registered_from: string | null;
    tax_region: 'rUK' | 'scotland';
    taxable_profit: number;
    vat_paid: number;            // input VAT paid to suppliers (reclaimable when registered)
    vat_net_position: number;    // collected − paid; negative means HMRC owes you a refund
    drawings: number;            // already paid to the owner in the period ("Pay Yourself"), from SQL
    loading: boolean;
    selectedMonth: string | null;
    isMonthView: boolean;       // a single month is selected (figures are that month's)
    isHistoricalView: boolean;  // the selected month has ended
    // Per-pot resilience status from the orchestrator JSON envelope.
    pot_status: PotStatusMap;
    snapshot_status: PotStatus | 'partial';
}

export interface OrgFinancialCache {
    org_id: string;
    total_income: number;
    total_expenses: number;
    total_vat_collected: number;
    total_vat_paid: number;
    cash_balance: number;
    last_updated: string;
}

interface FinancialContextType extends FinancialData {
    refresh: () => Promise<void>;
    setSelectedMonth: (month: string | null) => void;
}

const FinancialContext = createContext<FinancialContextType | undefined>(undefined);

interface SnapshotPot {
    value?: number;
    gross?: number;
    drawings?: number;
    underfunded_by?: number;
    is_underfunded?: boolean;
    status?: string;
}

/** The envelope both fn_get_financial_snapshot and fn_get_period_snapshot return. */
interface SnapshotEnvelope {
    status?: string;
    pots?: Partial<Record<'safe_to_spend' | 'tax_pot' | 'ni_pot' | 'vat_pot', SnapshotPot>>;
    working_capital?: Partial<WorkingCapital>;
    base?: Record<string, unknown>;
}

const toWorkingCapital = (wc: Partial<WorkingCapital> | null | undefined): WorkingCapital => ({
    parts_reserved: Number(wc?.parts_reserved ?? 0),
    parts_unreconciled: Number(wc?.parts_unreconciled ?? 0),
    bills_due_30d: Number(wc?.bills_due_30d ?? 0),
    total: Number(wc?.total ?? 0),
});

// All Safe-to-Spend math lives in SQL: pots.safe_to_spend.value is the NET
// headline; gross / underfunded come straight from the envelope. Values and
// per-pot status are passed through — no arithmetic, clamp or threshold in TS.
const mapSnapshot = (envelope: SnapshotEnvelope | null | undefined, working_capital: WorkingCapital) => {
    const pots = envelope?.pots ?? {};
    const base = (envelope?.base ?? {}) as Record<string, number | string | boolean | null | undefined>;
    const num = (v: unknown) => Number(v ?? 0);
    const safe_to_spend_net = pots.safe_to_spend?.value ?? 0;

    return {
        tax_pot: pots.tax_pot?.value ?? 0,
        ni_pot: pots.ni_pot?.value ?? 0,
        vat_pot: pots.vat_pot?.value ?? 0,
        raw_vat_liability: pots.vat_pot?.value ?? 0,
        safe_to_spend: pots.safe_to_spend?.gross ?? safe_to_spend_net,
        safe_to_spend_net,
        underfunded_by: pots.safe_to_spend?.underfunded_by ?? 0,
        is_underfunded: pots.safe_to_spend?.is_underfunded ?? false,
        working_capital,
        total_sales: num(base.total_sales),
        total_spending: num(base.total_spending),
        turnover_rolling_12m: num(base.turnover_rolling_12m),
        vat_threshold: num(base.vat_threshold),
        is_vat_registered: Boolean(base.is_vat_registered),
        vat_registered_from: (base.vat_registered_from as string | null | undefined) ?? null,
        tax_region: base.tax_region === 'scotland' ? 'scotland' as const : 'rUK' as const,
        taxable_profit: num(base.taxable_profit),
        vat_paid: num(base.vat_paid),
        vat_net_position: num(base.vat_net_position),
        drawings: num(pots.safe_to_spend?.drawings),
        pot_status: {
            safe_to_spend: (pots.safe_to_spend?.status ?? 'unknown') as PotStatus,
            tax_pot: (pots.tax_pot?.status ?? 'unknown') as PotStatus,
            ni_pot: (pots.ni_pot?.status ?? 'unknown') as PotStatus,
            vat_pot: (pots.vat_pot?.status ?? 'unknown') as PotStatus
        },
        snapshot_status: (envelope?.status ?? 'unknown') as PotStatus | 'partial'
    };
};

export const FinancialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { activeOrgId } = useVertical();
    const [data, setData] = useState<FinancialData>({
        safe_to_spend: 0,
        safe_to_spend_net: 0,
        underfunded_by: 0,
        is_underfunded: false,
        working_capital: { parts_reserved: 0, parts_unreconciled: 0, bills_due_30d: 0, total: 0 },
        tax_pot: 0,
        ni_pot: 0,
        vat_pot: 0,
        raw_vat_liability: 0,
        total_income: 0,
        total_expenses: 0,
        gross_income: 0,
        gross_expenses: 0,
        cash_balance: 0,
        turnover_rolling_12m: 0,
        vat_threshold: 0,
        is_vat_registered: false,
        vat_registered_from: null,
        tax_region: 'rUK',
        taxable_profit: 0,
        vat_paid: 0,
        vat_net_position: 0,
        drawings: 0,
        loading: true,
        selectedMonth: null,
        isMonthView: false,
        isHistoricalView: false,
        pot_status: {
            safe_to_spend: 'loading',
            tax_pot: 'loading',
            ni_pot: 'loading',
            vat_pot: 'loading'
        },
        snapshot_status: 'loading'
    });

    const [periodData, setPeriodData] = useState<{ month: string; data: Partial<FinancialData> } | null>(null);
    const [selectedMonth, setSelectedMonthState] = useState<string | null>(null);
    // Bumped on every ledger cache update, so a month being viewed (above all
    // the current one) refreshes live just like the tax-year figures.
    const [cacheRevision, setCacheRevision] = useState(0);

    // Debounce Ref
    const lastCalculationRef = useRef<number>(0);
    const DEBOUNCE_MS = 2000; // Only recalculate tax at most every 2 seconds

    const calculateLiabilities = useCallback(async (_cache: OrgFinancialCache) => {
        try {

            // Orchestrator returns a JSON envelope:
            // { status, pots: { <pot>: { value, status } }, base: { ... } }
            // Working capital is a separate, cheaper RPC — fetch in parallel so
            // safe_to_spend_net is ready in the same render pass as the gross.
            const [snapRes, wcRes] = await Promise.all([
                supabase.rpc('fn_get_financial_snapshot'),
                activeOrgId
                    ? supabase.rpc('fn_get_working_capital', { p_org_id: activeOrgId })
                    : Promise.resolve({ data: null, error: null }),
            ]);
            const { data: snap, error } = snapRes;
            if (error) throw error;

            const envelope = (Array.isArray(snap) ? snap[0] : snap) as SnapshotEnvelope | null;
            return mapSnapshot(envelope, toWorkingCapital(wcRes.data as Partial<WorkingCapital> | null));

        } catch (err) {
            console.error('Error calculating liabilities:', err);
            return {
                tax_pot: 0, ni_pot: 0, vat_pot: 0, raw_vat_liability: 0, safe_to_spend: 0,
                safe_to_spend_net: 0,
                underfunded_by: 0,
                is_underfunded: false,
                working_capital: { parts_reserved: 0, parts_unreconciled: 0, bills_due_30d: 0, total: 0 },
                total_sales: 0, total_spending: 0, turnover_rolling_12m: 0,
                vat_threshold: 0, is_vat_registered: false, vat_registered_from: null,
                tax_region: 'rUK' as const, taxable_profit: 0, vat_paid: 0, vat_net_position: 0, drawings: 0,
                pot_status: {
                    safe_to_spend: 'error' as PotStatus,
                    tax_pot: 'error' as PotStatus,
                    ni_pot: 'error' as PotStatus,
                    vat_pot: 'error' as PotStatus
                },
                snapshot_status: 'error' as PotStatus
            };
        }
    }, [activeOrgId]);

    // Throttle the recalculation, coalescing rather than dropping.
    //
    // There WAS a debounce, inside calculateLiabilities, and it did nothing: the
    // guard `if (now - lastCalculationRef.current < DEBOUNCE_MS) { }` had an
    // empty body, so every cache update ran the full recalculation — two RPCs,
    // one of them fn_get_financial_snapshot, the tax engine. It updated the ref
    // either way, so it read like a working debounce.
    //
    // Simply adding the missing `return` would have been wrong twice over. It
    // returns the figures the caller spreads into state, so a bare return is
    // `undefined` and the caller dereferences it. And a leading-edge throttle
    // DROPS the last update in a burst, which on Safe-to-Spend means showing a
    // stale headline number — worse than the redundant queries it saves.
    //
    // So: run immediately when the window is clear, otherwise remember the
    // latest cache and run once when the window closes. No update is lost and
    // the RPC is called at most once per window.
    const pendingCacheRef = useRef<OrgFinancialCache | null>(null);
    const trailingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyCacheUpdate = useCallback(async (cache: OrgFinancialCache) => {
        const liabilities = await calculateLiabilities(cache);

        setData(prev => ({
            ...prev,
            // ?? not ||. The RPC is the authority; the cache is only a
            // fallback for when it could not be reached. With `||`, an org whose
            // real figure is legitimately ZERO fell through to the cache — which
            // is exactly the case where the two are most likely to disagree, and
            // it silently showed a stale number as if it were live.
            total_income: liabilities.total_sales ?? cache.total_income,
            total_expenses: liabilities.total_spending ?? cache.total_expenses,
            gross_income: cache.total_income + cache.total_vat_collected,
            gross_expenses: cache.total_expenses + cache.total_vat_paid,
            cash_balance: cache.cash_balance,
            tax_pot: liabilities.tax_pot,
            ni_pot: liabilities.ni_pot,
            vat_pot: liabilities.vat_pot,
            raw_vat_liability: liabilities.raw_vat_liability,
            turnover_rolling_12m: liabilities.turnover_rolling_12m,
            vat_threshold: liabilities.vat_threshold,
            is_vat_registered: liabilities.is_vat_registered,
            vat_registered_from: liabilities.vat_registered_from,
            tax_region: liabilities.tax_region,
            taxable_profit: liabilities.taxable_profit,
            vat_paid: liabilities.vat_paid,
            vat_net_position: liabilities.vat_net_position,
            drawings: liabilities.drawings,
            safe_to_spend: liabilities.safe_to_spend,
            safe_to_spend_net: liabilities.safe_to_spend_net,
            underfunded_by: liabilities.underfunded_by,
            is_underfunded: liabilities.is_underfunded,
            working_capital: liabilities.working_capital,
            pot_status: liabilities.pot_status,
            snapshot_status: liabilities.snapshot_status,
            loading: false
        }));
        setCacheRevision(r => r + 1);
    }, [calculateLiabilities]);

    const processCacheUpdate = useCallback(async (cache: OrgFinancialCache) => {
        const now = Date.now();
        const elapsed = now - lastCalculationRef.current;

        if (elapsed >= DEBOUNCE_MS) {
            lastCalculationRef.current = now;
            await applyCacheUpdate(cache);
            return;
        }

        // Inside the window: keep only the newest cache and make sure exactly one
        // trailing run is scheduled.
        pendingCacheRef.current = cache;
        if (trailingTimerRef.current === null) {
            trailingTimerRef.current = setTimeout(() => {
                trailingTimerRef.current = null;
                const pending = pendingCacheRef.current;
                pendingCacheRef.current = null;
                if (pending) {
                    lastCalculationRef.current = Date.now();
                    void applyCacheUpdate(pending);
                }
            }, DEBOUNCE_MS - elapsed);
        }
    }, [applyCacheUpdate]);

    // A pending trailing run holds a closure over component state, so it must not
    // outlive the provider.
    useEffect(() => () => {
        if (trailingTimerRef.current !== null) {
            clearTimeout(trailingTimerRef.current);
            trailingTimerRef.current = null;
        }
    }, []);

    const fetchData = useCallback(async () => {
        if (!activeOrgId) return;

        const { data: cacheData, error } = await supabase
            .from('org_financial_cache')
            .select('*')
            .eq('org_id', activeOrgId)
            // maybeSingle, not single. An org has no cache row until its first
            // ledger write, and `.single()` answers that with HTTP 406 — which
            // PostgREST is entitled to do, but it puts a red line in every new
            // user's console on every page load and looks like a broken app to
            // anyone reading it. maybeSingle returns 200 + null for the same
            // state. Real failures still land in the branch below.
            .maybeSingle();

        if (error) {
            console.error('Financial cache fetch failed; figures may be stale:', error);
        }

        if (cacheData) {
            await processCacheUpdate(cacheData as OrgFinancialCache);
        } else {
            setData(prev => ({ ...prev, loading: false }));
        }
    }, [activeOrgId, processCacheUpdate]);

    useEffect(() => {
        if (!activeOrgId) return;

        // Initial Fetch
        fetchData();

        // Real-time Subscription
        const channel = supabase
            .channel(`financial-provider-${activeOrgId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'org_financial_cache',
                    filter: `org_id=eq.${activeOrgId}`
                },
                async (payload) => {
                    if (payload.new) {
                        await processCacheUpdate(payload.new as OrgFinancialCache);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [activeOrgId, fetchData, processCacheUpdate]);

    // A selected month gets the whole Safe-to-Spend picture for that month —
    // its own income, costs, tax, NI and VAT — from fn_get_period_snapshot, the
    // same envelope as the tax-year engine. Results are tagged with their month
    // so a slow response for a month no longer selected is never shown.
    useEffect(() => {
        if (!selectedMonth || !activeOrgId) return;
        let cancelled = false;

        const fetchPeriod = async () => {
            const { data: snap, error } = await supabase.rpc('fn_get_period_snapshot', {
                p_month: monthRange(selectedMonth).start,
            });
            if (error) reportError(error, 'financial-context.get-period-snapshot');
            if (cancelled) return;
            if (error) {
                // Flag the month as unavailable rather than leaving it loading
                // forever — and blank every money figure, so no tax-year amount
                // can stand in under the month's name on any card.
                setPeriodData({
                    month: selectedMonth,
                    data: {
                        safe_to_spend: 0, safe_to_spend_net: 0, underfunded_by: 0, is_underfunded: false,
                        working_capital: { parts_reserved: 0, parts_unreconciled: 0, bills_due_30d: 0, total: 0 },
                        tax_pot: 0, ni_pot: 0, vat_pot: 0, raw_vat_liability: 0,
                        total_income: 0, total_expenses: 0, gross_income: 0, gross_expenses: 0,
                        taxable_profit: 0, vat_paid: 0, vat_net_position: 0, drawings: 0,
                        pot_status: { safe_to_spend: 'error', tax_pot: 'error', ni_pot: 'error', vat_pot: 'error' },
                        snapshot_status: 'error',
                    },
                });
                return;
            }

            const envelope = (Array.isArray(snap) ? snap[0] : snap) as SnapshotEnvelope | null;
            const mapped = mapSnapshot(envelope, toWorkingCapital(envelope?.working_capital));
            const base = (envelope?.base ?? {}) as Record<string, unknown>;
            const { total_sales, total_spending, ...rest } = mapped;
            setPeriodData({
                month: selectedMonth,
                data: {
                    ...rest,
                    total_income: total_sales,
                    total_expenses: total_spending,
                    gross_income: Number(base.gross_sales ?? 0),
                    gross_expenses: Number(base.gross_spending ?? 0),
                },
            });
        };

        fetchPeriod();
        return () => { cancelled = true; };
    }, [selectedMonth, activeOrgId, cacheRevision]);

    const setSelectedMonth = useCallback((month: string | null) => {
        setSelectedMonthState(month);
    }, []);

    const currentMonth = monthKey(new Date());
    const period = selectedMonth && periodData?.month === selectedMonth ? periodData.data : null;

    const contextValue = {
        ...data,
        ...(period ?? {}),
        loading: data.loading || (selectedMonth !== null && period === null),
        selectedMonth,
        isMonthView: selectedMonth !== null,
        // A month that has ended: "right now" features (pay yourself, pace,
        // working capital) do not apply to it.
        isHistoricalView: selectedMonth !== null && selectedMonth < currentMonth,
        setSelectedMonth,
        // Re-read the tax year AND the selected month. fetchData alone only
        // reruns the month when a cache row changes, and a garage with no
        // ledger yet has no cache row — so "Try again" would never retry it.
        refresh: () => {
            setCacheRevision(r => r + 1);
            return fetchData();
        }
    };

    return (
        <FinancialContext.Provider value={contextValue}>
            {children}
        </FinancialContext.Provider>
    );
};

export const useFinancialContext = () => {
    const context = useContext(FinancialContext);
    if (context === undefined) {
        throw new Error('useFinancialContext must be used within a FinancialProvider');
    }
    return context;
};
